import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, Observer, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from 'app/auth/service';
import { User } from 'app/auth/models';
import { Amplify } from 'aws-amplify';
import * as AmplifyAuth from 'aws-amplify/auth';
import { AuthSession, JWT, SignInOutput, SignUpOutput } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';
import { ConfigService } from 'app/app-config.service';
import { Router } from '@angular/router';
import { LocalStorageKeys, StorageService } from 'app/main/services/storage/storage.service';
import { Store } from '@ngxs/store';
import { UserAction } from 'app/main/state/user.state';
import { OrganizationAction } from '@state/organization.state';
import { QuestionnaireAction } from '@state/questionnaire.state';
import { QuestionnaireConfigAction } from '@state/questionnaire.config.state';
import { SolutionAction } from '@state/solution.state';
import { UserHttpService } from '@services/datasources/user-http.service';
import { InputSanitizerService } from 'app/auth/helpers/input-sanitizer';
import { AuthSessionTransitionService } from './auth-session-transition.service';

/** What Amplify v5 resolved with on a successful update, which callers still receive. */
const SUCCESS = 'SUCCESS';

/**
 * The screens branch on Cognito's error code (`UserNotConfirmedException`, `CodeMismatchException`…).
 * Amplify v5 put it in `code`; v6 puts it in `name`.
 */
function cognitoError(err: unknown): { code?: string; message?: string } {
    if (err instanceof Error) {
        return { code: err.name, message: err.message };
    }
    return { message: String(err) };
}

@Injectable({ providedIn: 'root' })
export class CognitoAuthenticationService implements AuthenticationService {
    //public
    public currentUser$: Observable<User | null> = of(null);
    /** ID token claims of the signed-in user. */
    public authenticatedUser?: JWT['payload'];
    public authenticatedUser$ = new ReplaySubject<JWT['payload']>(1);

    //private
    private currentUserSubject: BehaviorSubject<User | null>;
    private userToValidate: { username: string; password: string } | undefined;
    client = this.configService.getClient;
    private clientName: string = this.client.name;

    private newPasswordPending = false;

    constructor(
        private configService: ConfigService,
        private router: Router,
        private localStorageService: StorageService,
        private store: Store,
        private userService: UserHttpService,
        private inputSanitizerService: InputSanitizerService,
        private authSessionTransition: AuthSessionTransitionService,
    ) {
        // Migration: Clear stale localStorage Cognito tokens when switching to sessionStorage
        // This prevents infinite loops when users have old tokens in localStorage but we now use sessionStorage
        this.migrateFromLocalStorageToSessionStorage();

        // The region comes from the user pool ID. No tenant configures a Hosted UI domain, so no OAuth.
        Amplify.configure({
            Auth: {
                Cognito: {
                    userPoolId: this.configService.getConfig.cognitoUserPoolId,
                    userPoolClientId: this.configService.getConfig.cognitoAppClientId,
                },
            },
        });
        // Store tokens in sessionStorage for better security. After configure, which resets it to localStorage.
        cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);

        const json = localStorageService.getGlobalItem<string>(LocalStorageKeys.currentUser, 1, undefined, 'session');
        this.currentUserSubject = new BehaviorSubject<User | null>(json ? (JSON.parse(json) as User) : null);

        this.currentUser$ = this.currentUserSubject.asObservable();

        this.authenticatedUser$.subscribe((user) => (this.authenticatedUser = user));

        this.currentUserSubject.subscribe((user) => {
            if (user) {
                this.currentUser$ = this.currentUserSubject.asObservable();
            }
        });

        //Get currently authenticated user if any
        AmplifyAuth.fetchAuthSession()
            .then((session) => {
                const idToken = session.tokens?.idToken;
                if (idToken) {
                    this.authenticatedUser$.next(idToken.payload);
                }
            })
            .catch(() => {
                // silently ignored — no authenticated user is a valid initial state
            });
    }

    public isNewPasswordRequired(): boolean {
        return this.newPasswordPending;
    }

    /**
     * Migration helper: Clears old Cognito tokens from localStorage when switching to sessionStorage.
     * This prevents an infinite loop scenario
     * This code should be safe to remove after a few months
     */
    private migrateFromLocalStorageToSessionStorage(): void {
        const clientId = this.configService.getConfig.cognitoAppClientId;

        const cognitoKeyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

        // Check if there are old Cognito tokens in localStorage
        const hasLocalStorageTokens = Object.keys(localStorage).some(
            (key) => key.startsWith(cognitoKeyPrefix) || key === LocalStorageKeys.currentUser,
        );

        // Check if there are tokens in sessionStorage
        const hasSessionStorageTokens = Object.keys(sessionStorage).some(
            (key) => key.startsWith(cognitoKeyPrefix) || key === LocalStorageKeys.currentUser,
        );

        // If tokens exist in localStorage but not in sessionStorage, we need to migrate
        if (hasLocalStorageTokens && !hasSessionStorageTokens) {
            // Clear all Cognito-related keys from localStorage
            Object.keys(localStorage)
                .filter((key) => key.startsWith(cognitoKeyPrefix) || key.startsWith('amplify-'))
                .forEach((key) => localStorage.removeItem(key));

            // Also clear our custom currentUser to ensure clean state
            this.localStorageService.removeItem(LocalStorageKeys.currentUser);
        }
    }

    // getter: currentUserValue
    public get currentUserValue(): User | null {
        return this.currentUserSubject.value;
    }

    public set currentUSerValue(user: User | null) {
        this.currentUserSubject.next(user);
    }

    /**
     * Check if the current user is authenticated
     * JWT token is refreshed during getSession
     */
    public isLoggedIn(refreshStorage?: boolean): Observable<boolean> {
        if (refreshStorage === undefined) {
            refreshStorage = true;
        }
        let isAuth = false;
        return new Observable((observer: Observer<boolean>) => {
            // Handle refresh of new user jwt token
            AmplifyAuth.fetchAuthSession().then(
                (session) => {
                    isAuth = session.tokens?.idToken !== undefined;
                    if (refreshStorage) {
                        this._handleCognitoSession(session);
                    }
                    observer.next(isAuth && (this.currentUserValue !== null || this.currentUserValue !== undefined));
                    observer.complete();
                },
                (_err) => {
                    observer.next(false);
                    observer.complete();
                    // observer.error(err.message || JSON.stringify(err));
                },
            );
        });
    }

    /**
     * User (described by its email) has forgotten its password
     * Request a verification to be able to reset it
     */
    public forgotPassword(email: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.resetPassword({ username: email }).then(
                (result) => {
                    const destination = result.nextStep.codeDeliveryDetails?.destination ?? '';
                    observer.next(destination);
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    /**
     * Confirm username registration with a verification code (email or sms). Email in our case
     */
    public confirmRegistration(username: string, verificationCode: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.confirmSignUp({ username, confirmationCode: verificationCode }).then(
                () => {
                    observer.next('ok');
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    /**
     * Reset password based on a verification code provided by the idp
     * for a specific username with a new password (newPassword)
     */
    public resetPassword(username: string, verificationCode: string, newPassword: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.confirmResetPassword({ username, confirmationCode: verificationCode, newPassword }).then(
                () => {
                    observer.next(SUCCESS);
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    public acceptTerms(value: number): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.updateUserAttributes({
                userAttributes: { 'custom:has_accepted_terms': value.toString() },
            }).then(
                () => {
                    // Refresh the tokens so the acceptance reaches the stored user: the old id token
                    // still says "not accepted", and a reload would send the user back to the terms
                    AmplifyAuth.fetchAuthSession({ forceRefresh: true }).then(
                        (session) => {
                            this._handleCognitoSession(session);
                            observer.next(SUCCESS);
                            observer.complete();
                        },
                        (err) => {
                            observer.error(cognitoError(err));
                        },
                    );
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    public updateUserLocale(locale: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.updateUserAttributes({ userAttributes: { locale: locale } }).then(
                () => {
                    this.localStorageService.setGlobalItem(LocalStorageKeys.localeUser, locale);
                    observer.next(SUCCESS);
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    private updateAttributes(
        firstName: string,
        lastName: string,
        phone_number: string,
        role: string,
    ): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.updateUserAttributes({
                userAttributes: {
                    phone_number: phone_number,
                    given_name: firstName,
                    family_name: lastName,
                    'custom:role': role,
                },
            }).then(
                () => {
                    // Refresh the tokens so the new claims reach the stored user
                    AmplifyAuth.fetchAuthSession({ forceRefresh: true }).then(
                        (session) => {
                            this._handleCognitoSession(session);
                            observer.next(SUCCESS);
                            observer.complete();
                        },
                        (err) => {
                            observer.error(cognitoError(err));
                        },
                    );
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    /**
     * Update user Attributes
     */
    public updateUserAttributes(
        firstName: string,
        lastName: string,
        phoneNumber: string,
        role: string,
    ): Observable<string> {
        return this.updateAttributes(firstName, lastName, phoneNumber, role).pipe(
            map((result) => {
                return result;
            }),
        );
    }

    private cognitoSignup(
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        userLanguage: string,
        companyName: string,
    ): Observable<SignUpOutput> {
        return new Observable((observer: Observer<SignUpOutput>) => {
            // If user language is undefined, set default language
            const hasLanguage = !!userLanguage;
            if (!hasLanguage) {
                userLanguage = 'en';
            }
            AmplifyAuth.signUp({
                username: email,
                password: password,
                options: {
                    userAttributes: {
                        email: email,
                        locale: userLanguage,
                        given_name: this.inputSanitizerService.sanitizeName(firstName),
                        family_name: lastName,
                        phone_number: '+3200000000000',
                        'custom:company_name': companyName,
                        'custom:is_initialized': '0',
                        'custom:has_accepted_terms': '1',
                    },
                },
            }).then(
                (result) => {
                    observer.next(result);
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    /**
     * User sign up
     */
    public signUp(
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        userLanguage: string,
        companyName: string,
    ): Observable<string> {
        // The email is the Cognito username
        return this.cognitoSignup(email, password, firstName, lastName, userLanguage, companyName).pipe(
            map(() => email),
        );
    }

    /**
     * Resend confirmation code for a specific username (email)
     */
    public resendCode(email: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.resendSignUpCode({ username: email }).then(
                (result) => {
                    observer.next(JSON.stringify(result));
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    private handleNewPassword(newPassword: string): Observable<SignInOutput> {
        return new Observable((observer: Observer<SignInOutput>) => {
            AmplifyAuth.confirmSignIn({ challengeResponse: newPassword }).then(
                (result) => {
                    observer.next(result);
                    observer.complete();
                    this.newPasswordPending = false;
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    /**
     * When the user is created by the admin
     * The user must change is password on first login
     * After the new password is handle, the user is logged in.
     * Store JWT in local storage
     * Handle Cognito session
     */
    public newPasswordRequired(newPassword: string): Observable<User | undefined> {
        return this.handleNewPassword(newPassword).pipe(
            mergeMap((result) => {
                if (result.isSignedIn) {
                    return this.signedInSession();
                }
                return of(undefined);
            }),
        );
    }

    private authenticate(email: string, password: string): Observable<SignInOutput> {
        return new Observable((observer: Observer<SignInOutput>) => {
            AmplifyAuth.signIn({ username: email, password })
                .catch(async (err: unknown) => {
                    // Amplify v6 refuses to sign in over a live session, which v5 replaced: drop the stale one and retry
                    if (cognitoError(err).code !== 'UserAlreadyAuthenticatedException') {
                        throw err;
                    }
                    await AmplifyAuth.signOut();
                    return AmplifyAuth.signIn({ username: email, password });
                })
                .then(
                    (result) => {
                        observer.next(result);
                        observer.complete();
                    },
                    (err) => {
                        observer.error(cognitoError(err));
                    },
                );
        });
    }

    private _storeCognitoUserSession(idToken: JWT): User {
        const current_user = new User();
        // login successful if there's a jwt token in the response
        const payload = idToken.payload as Record<string, string | undefined>;

        current_user.email = payload['email'] ?? '';

        if ('locale' in payload) {
            current_user.locale = payload['locale'];
        }
        if ('given_name' in payload) {
            current_user.firstName = payload['given_name'];
        }
        if ('family_name' in payload) {
            current_user.lastName = payload['family_name'];
        }
        if ('custom:role' in payload) {
            current_user.role = payload['custom:role'];
        }
        if ('custom:company_name' in payload) {
            current_user.companyName = payload['custom:company_name'];
        }
        // THIS IS TO ENSURE ALREADY REGISTERED USER ARE CONSIDERED AS INITIALIZED. THIS FIELD WOULD ALWAYS BE FILLED OUT IN THE REGISTER CASE
        current_user.isInitialized = payload['custom:is_initialized'] ?? '1';
        current_user.has_accepted_terms = payload['custom:has_accepted_terms'] ?? '0';
        current_user.token = idToken.toString();

        this.localStorageService.setGlobalItem(
            LocalStorageKeys.currentUser,
            JSON.stringify(current_user),
            1,
            undefined,
            'session',
        );
        this.localStorageService.setGlobalItem<string>(
            LocalStorageKeys.localeUser,
            current_user.locale ?? this.configService.getConfig.defaultLocale,
        );

        return current_user;
    }

    /**
     * User login
     * Store JWT in local storage
     * Handle Cognito session
     */
    public login(email: string, password: string): Observable<User | string> {
        return this.authenticate(email, password).pipe(
            mergeMap(({ nextStep }) => {
                switch (nextStep.signInStep) {
                    case 'DONE':
                        // In the login path the session is always present after successful auth, so
                        // signedInSession will not emit undefined. We narrow here to match the
                        // interface contract (Observable<User | string>).
                        return this.signedInSession().pipe(map((user) => user as User));
                    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                        this.newPasswordPending = true;
                        return of('newPasswordRequired');
                    // Amplify v6 returns these as steps where v5 threw: keep them errors for the login screen
                    case 'CONFIRM_SIGN_UP':
                        return throwError(() => ({ code: 'UserNotConfirmedException' }));
                    case 'RESET_PASSWORD':
                        return throwError(() => ({ code: 'PasswordResetRequiredException' }));
                    default:
                        return throwError(() => ({ code: nextStep.signInStep }));
                }
            }),
        );
    }

    /** Reads the session a successful sign-in has just stored, and makes it the current user. */
    private signedInSession(): Observable<User | undefined> {
        return from(AmplifyAuth.fetchAuthSession()).pipe(
            mergeMap((session) => {
                const idToken = session.tokens?.idToken;
                if (idToken) {
                    this.authenticatedUser$.next(idToken.payload);
                }
                return this._handleCognitoSession(session);
            }),
        );
    }

    private _handleCognitoSession(session: AuthSession): Observable<User | undefined> {
        const idToken = session.tokens?.idToken;

        if (idToken && idToken.toString() !== this.currentUserSubject.value?.token) {
            const current_user = this._storeCognitoUserSession(idToken);
            if (current_user.isInitialized === '0') {
                return this.userService
                    .initUser(
                        {
                            name: current_user.companyName ?? '',
                            firstName: current_user.firstName,
                            lastName: current_user.lastName,
                            email: current_user.email,
                            locale: current_user.locale,
                        },
                        this.clientName,
                    )
                    .pipe(
                        catchError(() => {
                            throw new Error('InitFailed');
                        }),
                        switchMap((r) => {
                            return new Observable((observer) => {
                                AmplifyAuth.updateUserAttributes({
                                    userAttributes: { 'custom:is_initialized': '1' },
                                })
                                    .then(() => {
                                        current_user.isInitialized = '1';
                                        observer.next(r);
                                        observer.complete();
                                    })
                                    .catch((err) => observer.error(err));
                            });
                        }),
                        tap(() => this.currentUserSubject.next(current_user)),
                        map(() => current_user),
                    );
            } else {
                // notify
                this.currentUserSubject.next(current_user);

                return of(current_user);
            }
        }

        return of(this.currentUserSubject.value ?? undefined);
    }

    /**
     * User logout
     */
    public logout(redirect?: boolean, sendToBackend: boolean = true) {
        if (redirect === undefined) {
            redirect = true;
        }

        return new Observable<void>((observer) => {
            this.authSessionTransition.beginLogoutSequence();
            this.store.dispatch([
                new OrganizationAction.Deactivate(),
                new QuestionnaireAction.Deactivate(),
                new QuestionnaireConfigAction.Deactivate(),
                new SolutionAction.Deactivate(),
            ]);
            // Clear the persisted admin-console selection so it never carries into the next session
            // (sessionStorage otherwise survives the logout redirect + reload).
            this.localStorageService.removeItem(LocalStorageKeys.selectedDedicatedAdminId, 'session');
            this.localStorageService.removeItem(LocalStorageKeys.selectedOrgId, 'session');
            // Call the backend logout endpoint first
            if (sendToBackend) {
                this.userService.logout().subscribe({
                    next: () => {
                        // remove user from session storage to log user out
                        this.localStorageService.removeItem(LocalStorageKeys.currentUser, 'session');
                        this.store.dispatch(new UserAction.Deactivate());
                        this.currentUserSubject.next(null);
                        // After backend logout, proceed with Cognito global signOut (revokes refresh tokens server-side)
                        AmplifyAuth.signOut({ global: true }).then(
                            () => {
                                if (!redirect) {
                                    this.authSessionTransition.endLogoutSequence();
                                    observer.next();
                                    observer.complete();
                                    return;
                                }
                                void this.router.navigate(['/auth/login']).then(
                                    () => {
                                        this.authSessionTransition.endLogoutSequence();
                                        window.location.reload();
                                    },
                                    () => {
                                        this.authSessionTransition.endLogoutSequence();
                                    },
                                );
                            },
                            (err) => {
                                console.error(err);
                                this.authSessionTransition.endLogoutSequence();
                            },
                        );
                    },
                    error: (err) => {
                        console.error('Backend logout error:', err);
                        // Even if backend logout fails, continue with Cognito global signOut
                        this.localStorageService.removeItem(LocalStorageKeys.currentUser, 'session');
                        AmplifyAuth.signOut({ global: true }).then(
                            () => {
                                if (!redirect) {
                                    this.authSessionTransition.endLogoutSequence();
                                    observer.next();
                                    observer.complete();
                                    return;
                                }
                                void this.router.navigate(['/auth/login']).then(
                                    () => {
                                        this.authSessionTransition.endLogoutSequence();
                                        window.location.reload();
                                    },
                                    () => {
                                        this.authSessionTransition.endLogoutSequence();
                                    },
                                );
                            },
                            (signOutErr) => {
                                console.error(signOutErr);
                                this.authSessionTransition.endLogoutSequence();
                            },
                        );
                    },
                });
            } else {
                // remove user from session storage to log user out
                this.localStorageService.removeItem(LocalStorageKeys.currentUser, 'session');
                this.store.dispatch(new UserAction.Deactivate());
                this.currentUserSubject.next(null);
                AmplifyAuth.signOut({ global: true }).then(
                    () => {
                        if (!redirect) {
                            this.authSessionTransition.endLogoutSequence();
                            observer.next();
                            observer.complete();
                            return;
                        }
                        void this.router.navigate(['/auth/login']).then(
                            () => {
                                this.authSessionTransition.endLogoutSequence();
                                window.location.reload();
                            },
                            () => {
                                this.authSessionTransition.endLogoutSequence();
                            },
                        );
                    },
                    (err) => {
                        console.error(err);
                        this.authSessionTransition.endLogoutSequence();
                    },
                );
            }
        });
    }

    /**
     * Refresh authenticated user session
     * Call this after updating user attributes via backend to sync the frontend
     */
    public refreshAuthenticatedUser(): Observable<void> {
        return new Observable((observer) => {
            AmplifyAuth.fetchAuthSession({ forceRefresh: true })
                .then((session) => {
                    const idToken = session.tokens?.idToken;
                    if (idToken) {
                        this.authenticatedUser$.next(idToken.payload);
                        this._storeCognitoUserSession(idToken);
                    }
                    observer.next();
                    observer.complete();
                })
                .catch((err) => {
                    observer.error(cognitoError(err));
                });
        });
    }

    /**
     * Change password
     */
    public changePassword(oldPassword: string, newPassword: string): Observable<string> {
        return new Observable((observer: Observer<string>) => {
            AmplifyAuth.updatePassword({ oldPassword, newPassword }).then(
                () => {
                    observer.next(SUCCESS);
                    observer.complete();
                },
                (err) => {
                    observer.error(cognitoError(err));
                },
            );
        });
    }

    public beginValidation(username: string, password: string): void {
        this.userToValidate = { username, password };
    }
    public getUserToValidate(): { username: string; password: string } | undefined {
        return this.userToValidate;
    }
    public endValidation() {
        this.userToValidate = undefined;
    }
}
